// =============================================================================
//  Copyright   : 2019 semantics GmbH
//  Date        : $Date: 2024-06-05 16:55:39 +0200 (Wed, 05 Jun 2024) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 143291 $
//  Purpose     : call config apis
// =============================================================================
// ---Third-party imports
import {put, throttle, takeEvery, takeLatest, select, putResolve, delay} from 'redux-saga/effects'

// ---Framework imports
import {Actions} from 'uix/registries'
import Api from 'uix/api'
import log from 'uix/logging'
import {entries, mapItems, dict, isObject, get} from 'ldash'

// ---Local imports
import {
  saveDomainConfig,
  saveDomainConfigValues,
  loadDomainConfigSpec,
  setDomainConfigSpec,
  setSpecLoading,
  loadDomainConfigSections,
  setDomainConfigSections,
  loadConfigOverlay,
  setConfigOverlay,
  rebuildDomainConfig,
} from './actions'

// ---Globals
function* onSave(error, dname) {
  if (error) {
    yield put(
      Actions.errorNotification({
        error: {errorMsg: error.errorMsg || error.error},
        header: 'Config Save Error',
      })
    )
  } else {
    let overlay = yield select(root => root.config.dconfigOverlays[dname])
    if (overlay) {
      // refresh loaded overlay
      //~ yield put(loadConfigOverlay(dname))
      yield _loadOverlay(dname)
    }
    //~ yield put(Actions.successNotification({message: 'Config saved.'}))
  }
}

function* _saveDomainConfig({payload}) {
  let {dname} = payload
  let {error} = yield Api.post(`/${dname}/api/updateDomainConfig`, {value: payload.text})
  yield onSave(error, dname)
}

function* _saveDomainConfigValues({payload}) {
  let {dname} = payload
  let {error} = yield Api.post(`/${dname}/api/updateDomainConfigValues`, payload.values)
  yield onSave(error, dname)
}

function* _loadDomainConfigSpec() {
  let isEmpty = yield select(root => root.config.dconfigSpec === null)
  if (!isEmpty) {
    return
  }
  yield putResolve(setSpecLoading())

  let {error, data} = yield Api.post(`/api/getDomainConfigSpec`)
  if (error) {
    yield put(Actions.errorNotification({error, header: 'Load Error'}))
  } else {
    // XXX: mutations ahead
    let o = 0
    let writeOrder = spec => {
      spec._sec_order_ = o
      o += 1
      spec._order_ = {}
      for (let [k, v] of entries(spec._spec_)) {
        spec._order_[k] = o
        o += 1
      }

      for (let [k, v] of entries(spec)) {
        if (!k.startsWith('_') || k === '__many__') {
          writeOrder(spec[k])
        }
      }
      return spec
    }

    yield put(setDomainConfigSpec(writeOrder(data)))
  }
}

function* _loadDomainConfigSections({payload}) {
  let {sections, dname} = payload
  let dconfigEditor = yield select(root => root.config.dconfigEditor[dname])
  let unloaded = sections.filter(name => dconfigEditor?.[name] !== 'LOADING')

  if (unloaded.length > 0) {
    let loadingSignal = dict(unloaded.map(name => [name, 'LOADING']))

    yield putResolve(setDomainConfigSections(dname, loadingSignal))
    let {error, data} = yield Api.post(`/${dname}/api/getDomainConfigSections`, {
      sections: sections.join(','),
    })
    if (error) {
      yield put(Actions.errorNotification({error, header: 'Sections: Load Error'}))
    } else {
      yield put(setDomainConfigSections(dname, data))
    }
  }
}

function* _loadOverlay(dname) {
  let {error, data} = yield Api.post(`/${dname}/api/getConfigOverlay`)
  if (error) {
    yield put(Actions.errorNotification({error, header: 'Overlay: Load Error'}))
  } else {
    yield put(setConfigOverlay(dname, data))
  }
}

function* _loadConfigOverlay({payload}) {
  let {dname} = payload

  let currOverlay = yield select(root => root.config.dconfigOverlays[dname])
  if (currOverlay?.loading) {
    return
  }

  yield putResolve(setConfigOverlay(dname, {...currOverlay, loading: true}))
  yield _loadOverlay(dname)
}

function* _rebuildDomainConfig({payload}) {
  let {dname} = payload
  let {error, data} = yield Api.post(`/${dname}/api/rebuildDomainConfig`, {}, {timeout: Infinity})
  if (error) {
    yield put(Actions.errorNotification({error, header: 'Rebuild Error'}))
    yield put(Actions.rebuildDomainConfigDone([]))
  } else {
    log.info('rebuild in %o secs', data.duration.toFixed(1))
    // update dirtyness
    yield put(Actions.setDirtyConfigState(dict(data.dirty.map(dname => [dname, true]))))
    yield put(Actions.rebuildDomainConfigDone(data.reloaded))
    // refresh all parts
    yield put(Actions.go())
  }
}

export const sagas = {
  saveDomainConfig: takeEvery(saveDomainConfig, _saveDomainConfig),
  saveDomainConfigValues: takeEvery(saveDomainConfigValues, _saveDomainConfigValues),
  loadDomainConfigSpec: throttle(1000, loadDomainConfigSpec, _loadDomainConfigSpec),
  loadDomainConfigSections: takeEvery(loadDomainConfigSections, _loadDomainConfigSections),
  loadConfigOverlay: takeEvery(loadConfigOverlay, _loadConfigOverlay),
  rebuildDomainConfig: takeEvery(rebuildDomainConfig, _rebuildDomainConfig),
}
